import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { AlertTriangle, Shield, XCircle, CheckCircle2 } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const DisclaimerPage = () => {
  usePageSEO();
  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fdf2f4] rounded-full mb-4">
              <AlertTriangle className="w-8 h-8 text-[#b89a6d]" />
            </div>
            <h1 className="text-4xl font-bold text-[#3a2a26] mb-4">Disclaimer</h1>
            <p className="text-[#7a6b62]">Last updated: February 27, 2026</p>
          </div>

          <div className="space-y-8">
            {/* Main Disclaimer */}
            <section className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#b89a6d] mb-4">Important Notice</h2>
              <p className="text-[#5a4a40] leading-relaxed text-lg">
                Products sold on this website are for <strong className="text-[#3a2a26]">general wellness and research purposes only</strong>. 
                They are <strong className="text-[#3a2a26]">not intended to diagnose, treat, cure, or prevent any disease</strong>.
              </p>
            </section>

            {/* Not Medical Advice */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <XCircle className="w-6 h-6 text-[#c47267] mr-3" />
                Not Medical Advice
              </h2>
              <div className="text-[#5a4a40] space-y-4">
                <p>
                  The information provided on this website is for general informational purposes only and 
                  should not be considered medical advice. We are not medical professionals, and our products 
                  are not prescription medications.
                </p>
                <p>
                  <strong className="text-[#3a2a26]">Always consult with a qualified healthcare professional</strong> before 
                  starting any new supplement, wellness routine, or making changes to your health routine.
                </p>
              </div>
            </section>

            {/* What We Don't Claim */}
            <section className="bg-white border border-[#e8d4d0] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <XCircle className="w-6 h-6 text-[#c47267] mr-3" />
                What We Do NOT Claim
              </h2>
              <ul className="text-[#5a4a40] space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">✗</span>
                  <span>Our products can cure any disease or medical condition</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">✗</span>
                  <span>Our products can heal injuries or replace medical treatment</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">✗</span>
                  <span>Our products are prescription equivalents or replacements</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">✗</span>
                  <span>Guaranteed results or outcomes from using our products</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">✗</span>
                  <span>Clinically proven muscle repair or hormone therapy benefits</span>
                </li>
              </ul>
            </section>

            {/* What We Do Provide */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <CheckCircle2 className="w-6 h-6 text-[#b89a6d] mr-3" />
                What We Do Provide
              </h2>
              <ul className="text-[#5a4a40] space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">✓</span>
                  <span>Consumer wellness and wellness products</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">✓</span>
                  <span>Transparent ingredient labeling</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">✓</span>
                  <span>Clear product information and directions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">✓</span>
                  <span>Educational content about med spa and wellness</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">✓</span>
                  <span>Clear consumer policies (refunds, shipping, privacy)</span>
                </li>
              </ul>
            </section>

            {/* FDA Notice */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Shield className="w-6 h-6 text-[#b89a6d] mr-3" />
                FDA Notice
              </h2>
              <p className="text-[#5a4a40] leading-relaxed">
                These statements have not been evaluated by the Food and Drug Administration. 
                Our products are not intended to diagnose, treat, cure, or prevent any disease. 
                The FDA has not approved our products as drugs or medical devices.
              </p>
            </section>

            {/* Individual Results */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Individual Results May Vary</h2>
              <p className="text-[#5a4a40] leading-relaxed">
                Any testimonials or product descriptions on this site are based on individual experiences 
                and are not representative of all users. Individual results may vary. We make no guarantees 
                regarding specific outcomes or results from using our products.
              </p>
            </section>

            {/* Assumption of Risk */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Assumption of Risk</h2>
              <p className="text-[#5a4a40] leading-relaxed">
                By purchasing and using our products, you acknowledge that you understand and accept the risks 
                associated with dietary supplements and wellness products. You agree to consult with a healthcare 
                professional before use, especially if you have any medical conditions, are taking medications, 
                are pregnant or nursing, or are under 18 years of age.
              </p>
            </section>

            {/* Related Links */}
            <div className="flex flex-wrap gap-4 justify-center pt-4">
              <Link 
                to="/terms" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Terms of Service
              </Link>
              <Link 
                to="/privacy" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/refund" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DisclaimerPage;
